/**
 * Onboarding Form Component
 * Modal form for adding/editing onboarding records
 */

import React, { useState, useEffect } from 'react';
import { PHASE_STATUS_OPTIONS } from '../utils/onboardingColumns';
import './OnboardingForm.css';

function OnboardingForm({ onboarding, onSave, onClose }) {
  const [formData, setFormData] = useState({
    phase_status: 'New',
    employee_name: '',
    personal_email: '',
    personal_phone: '',
    date_of_birth: '',
    start_date: '',
    job_title: '',
    department: '',
    sc_department: '',
    target_store: '',
    training_store: '',
    recruiter: '',
    hiring_manager: '',
    flm_name: '',
    hr_generalist: '',
    store_manager: '',
    vp: '',
    director: '',
    mum: '',
    allowances: '',
    equipment: '',
    background_check: false,
    support_ticket: false,
    new_hire_docs: false,
    welcome_email: false,
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (onboarding) {
      setFormData({
        phase_status: onboarding.phase_status || 'New',
        employee_name: onboarding.employee_name || '',
        personal_email: onboarding.personal_email || '',
        personal_phone: onboarding.personal_phone || '',
        date_of_birth: onboarding.date_of_birth || '',
        start_date: onboarding.start_date || '',
        job_title: onboarding.job_title || '',
        department: onboarding.department || '',
        sc_department: onboarding.sc_department || '',
        target_store: onboarding.target_store || '',
        training_store: onboarding.training_store || '',
        recruiter: onboarding.recruiter || '',
        hiring_manager: onboarding.hiring_manager || '',
        flm_name: onboarding.flm_name || '',
        hr_generalist: onboarding.hr_generalist || '',
        store_manager: onboarding.store_manager || '',
        vp: onboarding.vp || '',
        director: onboarding.director || '',
        mum: onboarding.mum || '',
        allowances: onboarding.allowances || '',
        equipment: onboarding.equipment || '',
        background_check: onboarding.background_check || false,
        support_ticket: onboarding.support_ticket || false,
        new_hire_docs: onboarding.new_hire_docs || false,
        welcome_email: onboarding.welcome_email || false,
        notes: onboarding.notes || ''
      });
    }
  }, [onboarding]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    // Required fields
    if (!formData.employee_name?.trim()) {
      newErrors.employee_name = 'Employee name is required';
    }

    if (!formData.job_title?.trim()) {
      newErrors.job_title = 'Job title is required';
    }

    // Email validation
    if (formData.personal_email && !isValidEmail(formData.personal_email)) {
      newErrors.personal_email = 'Invalid email address';
    }

    // Start date must be Monday
    if (formData.start_date) {
      const date = new Date(formData.start_date);
      const dayOfWeek = date.getUTCDay();
      if (dayOfWeek !== 1) {
        newErrors.start_date = 'Start date must be a Monday';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setSaving(true);

      // Sanitize form data: convert empty strings to null for date fields
      const sanitizedData = { ...formData };
      const dateFields = ['date_of_birth', 'start_date'];

      dateFields.forEach(field => {
        if (sanitizedData[field] === '' || sanitizedData[field] === undefined) {
          sanitizedData[field] = null;
        }
      });

      await onSave(sanitizedData);
    } catch (err) {
      setErrors({ submit: 'Failed to save onboarding record. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content onboarding-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{onboarding ? 'Edit Onboarding Record' : 'Add New Onboarding Record'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="onboarding-form">
          {errors.submit && (
            <div className="alert alert-error">{errors.submit}</div>
          )}

          {/* Phase & Personal Information */}
          <div className="form-section">
            <h3>Phase & Personal Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>
                  Phase Status <span className="required">*</span>
                </label>
                <select
                  value={formData.phase_status}
                  onChange={(e) => handleChange('phase_status', e.target.value)}
                >
                  {PHASE_STATUS_OPTIONS.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>
                  Employee Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={formData.employee_name}
                  onChange={(e) => handleChange('employee_name', e.target.value)}
                  className={errors.employee_name ? 'error' : ''}
                />
                {errors.employee_name && (
                  <span className="error-message">{errors.employee_name}</span>
                )}
              </div>

              <div className="form-group">
                <label>Personal Email</label>
                <input
                  type="email"
                  value={formData.personal_email}
                  onChange={(e) => handleChange('personal_email', e.target.value)}
                  className={errors.personal_email ? 'error' : ''}
                />
                {errors.personal_email && (
                  <span className="error-message">{errors.personal_email}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Personal Phone</label>
                <input
                  type="tel"
                  value={formData.personal_phone}
                  onChange={(e) => handleChange('personal_phone', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Date of Birth</label>
                <input
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => handleChange('date_of_birth', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Start Date (Must be Monday)</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleChange('start_date', e.target.value)}
                  className={errors.start_date ? 'error' : ''}
                />
                {errors.start_date && (
                  <span className="error-message">{errors.start_date}</span>
                )}
              </div>
            </div>
          </div>

          {/* Job Information */}
          <div className="form-section">
            <h3>Job Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>
                  Job Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={formData.job_title}
                  onChange={(e) => handleChange('job_title', e.target.value)}
                  className={errors.job_title ? 'error' : ''}
                />
                {errors.job_title && (
                  <span className="error-message">{errors.job_title}</span>
                )}
              </div>

              <div className="form-group">
                <label>Department</label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => handleChange('department', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>SC Department</label>
                <input
                  type="text"
                  value={formData.sc_department}
                  onChange={(e) => handleChange('sc_department', e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Target Store</label>
                <input
                  type="text"
                  value={formData.target_store}
                  onChange={(e) => handleChange('target_store', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Training Store</label>
                <input
                  type="text"
                  value={formData.training_store}
                  onChange={(e) => handleChange('training_store', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Recruitment Information */}
          <div className="form-section">
            <h3>Recruitment Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Recruiter</label>
                <input
                  type="text"
                  value={formData.recruiter}
                  onChange={(e) => handleChange('recruiter', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Hiring Manager</label>
                <input
                  type="text"
                  value={formData.hiring_manager}
                  onChange={(e) => handleChange('hiring_manager', e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>FLM Name (Auto-populated)</label>
                <input
                  type="text"
                  value={formData.flm_name}
                  disabled
                  className="readonly-field"
                  title="This field is auto-populated from the system"
                />
              </div>

              <div className="form-group">
                <label>HR Generalist (Auto-populated)</label>
                <input
                  type="text"
                  value={formData.hr_generalist}
                  disabled
                  className="readonly-field"
                  title="This field is auto-populated from the system"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Store Manager (Auto-populated)</label>
                <input
                  type="text"
                  value={formData.store_manager}
                  disabled
                  className="readonly-field"
                  title="This field is auto-populated from the system"
                />
              </div>

              <div className="form-group">
                <label>VP (Auto-populated)</label>
                <input
                  type="text"
                  value={formData.vp}
                  disabled
                  className="readonly-field"
                  title="This field is auto-populated from the system"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Director (Auto-populated)</label>
                <input
                  type="text"
                  value={formData.director}
                  disabled
                  className="readonly-field"
                  title="This field is auto-populated from the system"
                />
              </div>

              <div className="form-group">
                <label>MUM (Auto-populated)</label>
                <input
                  type="text"
                  value={formData.mum}
                  disabled
                  className="readonly-field"
                  title="This field is auto-populated from the system"
                />
              </div>
            </div>
          </div>

          {/* Benefits & Equipment */}
          <div className="form-section">
            <h3>Benefits & Equipment</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Allowances</label>
                <input
                  type="text"
                  value={formData.allowances}
                  onChange={(e) => handleChange('allowances', e.target.value)}
                  placeholder="e.g., Car, Phone"
                />
              </div>

              <div className="form-group">
                <label>Equipment</label>
                <input
                  type="text"
                  value={formData.equipment}
                  onChange={(e) => handleChange('equipment', e.target.value)}
                  placeholder="e.g., Laptop"
                />
              </div>
            </div>
          </div>

          {/* Onboarding Checklist */}
          <div className="form-section">
            <h3>Onboarding Checklist</h3>
            <div className="form-row checklist-row">
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.background_check}
                    onChange={(e) => handleChange('background_check', e.target.checked)}
                  />
                  Background Check
                </label>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.support_ticket}
                    onChange={(e) => handleChange('support_ticket', e.target.checked)}
                  />
                  Support Ticket
                </label>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.new_hire_docs}
                    onChange={(e) => handleChange('new_hire_docs', e.target.checked)}
                  />
                  New Hire Docs
                </label>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.welcome_email}
                    onChange={(e) => handleChange('welcome_email', e.target.checked)}
                  />
                  Welcome Email
                </label>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="form-section">
            <h3>Notes</h3>
            <div className="form-row">
              <div className="form-group full-width">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows="4"
                  placeholder="Additional notes..."
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default OnboardingForm;
